<script lang="ts">
  import * as LDClient from 'launchdarkly-js-client-sdk'
  import { computed, inject, provide, ref, type InjectionKey } from 'vue'

  export const FEATURE_FLAGS = {
    IS_DEV: import.meta.env.DEV,
    IS_PROD: import.meta.env.PROD ?? import.meta.env.MODE === 'production',
    IS_TEST: import.meta.env.MODE === 'test',
    IS_STAGING: import.meta.env.MODE === 'staging'
  } as const

  export interface FeatureFlagMeta {
    title: string
    description: string
  }

  /**
   * Registry of all known feature flags with human-readable metadata.
   * Adding a new flag here automatically extends the `FeatureFlag` type.
   */
  export const FEATURE_FLAG_REGISTRY = {
    'mail-prototype': {
      title: 'Mail Prototype',
      description: 'Enables the experimental mail integration.'
    }
  } as const satisfies Record<string, FeatureFlagMeta>

  export type KnownFeatureFlag = keyof typeof FEATURE_FLAG_REGISTRY

  export type FeatureFlag = (string & {}) | KnownFeatureFlag

  export function createFeatureFlagContext() {
    const _client = ref<LDClient.LDClient | null>(null)

    const _allFlags = ref<Record<string, boolean | number | string>>({})

    const _disabledFlags = ref<() => string[]>(() => [])

    function _updateAllFlags(
      changes: Record<string, { current: boolean; previous: boolean }>
    ) {
      const newFlags = Object.entries(changes).reduce(
        (acc, [key, value]) => {
          acc[key] = value.current
          return acc
        },
        {} as Record<string, boolean>
      )
      _allFlags.value = newFlags
    }

    const computedAllFlags = computed(() => {
      return _allFlags.value
    })

    async function _initialize(contexts: {
      user: {
        key: string
        name: string
      }
      tenant: {
        key: string
        name: string
      }
      database?: {
        key: string
        name: string
      }
    }) {
      const mode = import.meta.env.MODE

      if (mode === 'test') {
        return
      }
      if (_client.value) {
        await _client.value.close()
        _client.value = null
      }
      const getClientSideID = () => {
        if (mode === 'development') {
          return '68c00eccaabf1b09c4b1216b'
        }
        if (mode === 'staging') {
          return '68bad1a5a6d37f09b75d3794'
        }
        if (mode === 'production') {
          return '68a59c8a10d5e0098a4e1e35'
        }
        throw new Error('Client side ID is not set')
      }
      const clientSideID = getClientSideID()
      if (!clientSideID) {
        throw new Error('Client side ID is not set')
      }
      // const context = {
      //   kind: 'user',
      //   key: 'context-key-123abc'
      // }
      const context: LDClient.LDMultiKindContext = {
        kind: 'multi',
        ...contexts
      }
      // Dynamically imported: the observability plugin is ~340 kB and must
      // not sit on the startup-critical path (it would be module-preloaded).
      const { default: Observability } =
        await import('@launchdarkly/observability')
      const client = LDClient.initialize(clientSideID, context, {
        // the observability plugins require JS SDK v3.7+
        plugins: [new Observability() /* , new SessionReplay() */],
        streaming: true
      })

      try {
        await client.waitForInitialization(5)
        _allFlags.value = client.allFlags()
        // await client.waitUntilReady()
        _client.value = client
        _client.value.on('change', function (changes) {
          _updateAllFlags(changes ?? {})
        })
        // proceedWithSuccessfullyInitializedClient();
      } catch {
        // Client failed to initialize or timed out — variation() calls
        // return fallback values until initialization completes.
      }
    }

    function getMailPrototypeCredentials() {
      const text = _allFlags.value['mail-prototype']
      if (typeof text === 'object') {
        return text as {
          username: string
          password: string
        }
      }
      return {
        username: null,
        password: null
      }
    }

    function setDisabledFlags(getter: () => string[]) {
      _disabledFlags.value = getter
    }

    function hasFeatureFlag(
      featureFlag: FeatureFlag,
      defaultValue: boolean = false
    ) {
      if (featureFlag === 'mail-prototype') {
        const credentials = getMailPrototypeCredentials()
        if (credentials.username && credentials.password) {
          return !_disabledFlags.value().includes(featureFlag)
        }
        return false
      }
      if (!_client.value) {
        return defaultValue
      }
      const granted = Boolean(_allFlags.value[featureFlag] ?? defaultValue)
      if (!granted) return false
      return !_disabledFlags.value().includes(featureFlag)
    }

    return {
      // ldClient: _client,
      allFlags: computedAllFlags,
      _initialize,
      setDisabledFlags,
      hasFeatureFlag,
      getMailPrototypeCredentials
    }
  }

  export type FeatureFlagContext = ReturnType<typeof createFeatureFlagContext>

  export const featureFlagKey = Symbol(
    'gs-feature-flag'
  ) as InjectionKey<FeatureFlagContext>

  export function useFeatureFlag(): Omit<FeatureFlagContext, '_initialize'> {
    const instance = inject(featureFlagKey)

    if (!instance) {
      throw new Error('Feature flag context not found')
    }

    return instance
  }

  export function _useFeatureFlag(): FeatureFlagContext {
    const instance = inject<FeatureFlagContext>(featureFlagKey)

    if (!instance) {
      throw new Error('Feature flag context not found')
    }

    return instance
  }
</script>
<script setup lang="ts">
  const featureFlagContext = createFeatureFlagContext()
  provide(featureFlagKey, featureFlagContext)

  defineExpose(featureFlagContext)
</script>
<template>
  <slot />
</template>
